import { stripHtmlTags } from "@msys/common";
import { RichTextValue } from "@msys/ui";
import {
  CloseFullscreen as CloseFullscreenIcon,
  OpenInFull as OpenInFullIcon,
} from "@mui/icons-material";
import { Box, IconButton, Popover, Stack, Typography } from "@mui/material";
import type {
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridTreeNodeWithRender,
} from "@mui/x-data-grid-premium";
import React from "react";
import { DataGridRichTextHtmlEditCell } from "./DataGridRichTextEditCell.js";

const MIN_POPOVER_WIDTH = 400;

export const DataGridDescriptionViewCell = ({
  params,
  canEdit,
}: {
  params: GridRenderCellParams<any, string, any, GridTreeNodeWithRender>;
  canEdit: boolean;
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [isExpanded, setIsExpanded] = React.useState(false);

  const containerRef = React.useCallback((node: HTMLDivElement | null) => {
    if (node) setAnchorEl(node);
  }, []);

  const handleContentClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!canEdit) return;
      params.api.startCellEditMode({ id: params.id, field: params.field });
    },
    [canEdit, params]
  );

  const handleExpandClick = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsExpanded(true);
  }, []);

  const handleCollapseClick = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsExpanded(false);
  }, []);

  const handleClose = React.useCallback(() => {
    setIsExpanded(false);
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{ width: "100%", height: "100%", position: "relative" }}
    >
      <Stack
        onClick={handleContentClick}
        direction="row"
        spacing={0.5}
        sx={{
          alignItems: "center",
          width: "100%",
          height: "100%",
          overflow: "hidden",
          cursor: canEdit ? "pointer" : "default",
          zIndex: 1,
          position: "relative",
          display: "flex",
        }}
      >
        <Typography
          variant="body2"
          noWrap
          sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {stripHtmlTags(params.value ?? "", true)
            .split("\n")
            .find(line => line.trim() !== "") ?? ""}
        </Typography>
        <IconButton size="small" onClick={handleExpandClick}>
          <OpenInFullIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Popover
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        disableScrollLock
        transitionDuration={0}
        open={isExpanded && !!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            "doc-data-grid-popover": "true",
            sx: {
              width: Math.max(
                MIN_POPOVER_WIDTH,
                (anchorEl?.offsetWidth ?? 0) + 2
              ),
              mt: "-1px",
              ml: "-1px",
            },
            onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
          },
        }}
      >
        <Box sx={{ position: "relative" }}>
          <Box
            onClick={handleContentClick}
            sx={{
              cursor: canEdit ? "pointer" : "default",
              zIndex: 1,
              position: "relative",
              p: 0.5,
            }}
          >
            <RichTextValue htmlContent={params.value ?? ""} />
          </Box>
          <IconButton
            size="small"
            onClick={handleCollapseClick}
            sx={{ position: "absolute", bottom: 1, right: 1, zIndex: 2 }}
          >
            <CloseFullscreenIcon fontSize="small" />
          </IconButton>
        </Box>
      </Popover>
    </Box>
  );
};

export const DataGridDescriptionEditCell = (
  params: GridRenderEditCellParams
) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const containerRef = React.useCallback((node: HTMLDivElement | null) => {
    if (node) setAnchorEl(node);
  }, []);

  const handleClose = React.useCallback(() => {
    params.api.stopCellEditMode({
      id: params.id,
      field: params.field,
      ignoreModifications: true,
    });
  }, [params]);

  return (
    <>
      <Box
        ref={containerRef}
        sx={{ width: "100%", height: "100%", position: "relative" }}
      />
      <Popover
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        disableScrollLock
        transitionDuration={0}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            "doc-data-grid-popover": "true",
            sx: {
              width: Math.max(
                MIN_POPOVER_WIDTH,
                (anchorEl?.offsetWidth ?? 0) + 2
              ),
              mt: "-1px",
              ml: "-1px",
            },
          },
        }}
      >
        <DataGridRichTextHtmlEditCell {...params} />
      </Popover>
    </>
  );
};
